<template>
  <div :class="$style.container">
    <div :class="$style.wrapper">
      <div
        v-if="$store.getters['user/canEdit']"
        :class="$style.toggleContainer"
      >
        {{ $t('vnc.control') }}
        <toggle-button
          v-if="rfb"
          v-model="control"
          :value="control"
          :sync="true"
        />
      </div>
      <button
        v-for="[key, value] in Object.entries(taskType)"
        :key="key"
        :class="$style.actionButton"
        @click="addTaskForComputers(key)"
      >
        <img
          :class="$style.inline"
          :src="require('@/assets/images/icons/navigation/' + value.svg + '.svg')"
        >
        <div :class="[$style.inline, $style.buttonText]">
          {{ value.text }}
        </div>
      </button>

      <button
        :class="[$style.actionButton, { [$style.recordingButton]: isRecording }]"
        @click="toggleRecording"
      >
        <span
          v-if="isRecording"
          :class="$style.recordingIndicator"
        />

        <span
          :class="[
            $style.recordingIcon,
            {
              [$style.playIcon]: !isRecording,
              [$style.stopIcon]: isRecording,
            },
          ]"
        />

        <div :class="[$style.inline, $style.buttonText]">
          {{ isRecording ? recordingTime : $t('vnc.record') }}
        </div>
      </button>
    </div>
    <div
      ref="screenArea"
      :class="$style.screenArea"
    >
      <div :class="$style.description">
        <div :class="$style.status">
          {{ name }}
        </div>

        <div
          v-if="status === 'connected'"
          :class="[$style.status, $style.green]"
        >
          {{ $t('vnc.connected') }}
        </div>
        <div
          v-if="status === 'disconnected'"
          :class="[$style.status, $style.red]"
        >
          {{ $t('vnc.disabled') }}
        </div>
        <div
          v-if="status === 'error'"
          :class="[$style.status, $style.red]"
        >
          {{ $t('vnc.error') }}
        </div>
        <div
          v-if="status === 'connecting'"
          :class="[$style.status, $style.yellow]"
        >
          {{ $t('vnc.connection') }}
        </div>
      </div>

      <div :class="$style.zoomBar">
        <button
          :class="$style.zoomBtn"
          @click="changeZoom(-0.25)"
        >
          −
        </button>
        <span :class="$style.zoomValue">{{ zoomPercent }}%</span>
        <button
          :class="$style.zoomBtn"
          @click="changeZoom(0.25)"
        >
          +
        </button>

        <button
          :class="$style.zoomBtn"
          @click="resetZoom"
        >
          100%
        </button>
        <button
          :class="$style.zoomBtn"
          @click="fitToScreen"
        >
          {{ $t('vnc.zoomFit') }}
        </button>

        <button
          :class="$style.zoomBtn"
          @click="focusHalf('left')"
        >
          {{ $t('vnc.screenN', { n: 1 }) }}
        </button>
        <button
          :class="$style.zoomBtn"
          @click="focusHalf('right')"
        >
          {{ $t('vnc.screenN', { n: 2 }) }}
        </button>

        <button
          :class="$style.zoomBtn"
          @click="toggleFullscreen"
        >
          {{ isFullscreen ? $t('vnc.exitFullscreen') : $t('vnc.enterFullscreen') }}
        </button>

        <span :class="$style.zoomHint">
          {{ $t('vnc.zoomHint') }}
        </span>
      </div>

      <div
        ref="screenScroll"
        :class="$style.screenScroll"
        @wheel.ctrl.prevent="onWheelZoom"
      >
        <div
          id="screen"
          ref="screen"
          :class="$style.sreenContainer"
          :style="screenStyle"
        />
      </div>
    </div>

    <message-text-modal
      v-if="isOpenModal"
      id="MessageText"
      :computer="currentComputer"
      :type="'11'"
      :is-loading="isLoading"
      :class="$style.modal"
      @close="closeModal('MessageText')"
      @add="addMessageTask"
    />
  </div>
</template>

<script>
  import MessageTextModal from '@/views/tasks/message-text.vue';
  import RFB from '@novnc/novnc';
  import jwt from 'jsonwebtoken';
  import { getComputersAndVncKey, forceFileDownload } from '@/api/methods/computers';
  import Vue from 'vue';
  import { ToggleButton } from 'vue-js-toggle-button';
  import { addTask } from '@/api/methods/tasks';
  import dayjs from 'dayjs';

  Vue.component('ToggleButton', ToggleButton);

  export default {
    components: {
      MessageTextModal,
    },
    data() {
      return {
        isRecording: false,
        mediaRecorder: null,
        recordingStream: null,
        recordingCanvas: null,
        recordingContext: null,
        recordingAnimationFrame: null,
        recordedChunks: [],
        recordingStartedAt: null,
        recordingDuration: 0,
        recordingTimer: null,
        fullscreenChangeHandler: null,
        fullscreenKeyHandler: null,
        screenPointerHandler: null,
        isLoading: false,
        isOpenModal: false,
        currentComputer: null,
        payloads: [],
        name: '',
        status: 'connecting',
        rfb: null,
        host: null,
        port: null,
        sourcePort: null,
        temp: 0,
        zoom: 1,
        scrollW: 0,
        scrollH: 0,
        isFullscreen: false,
        taskType: {
          10: {
            text: this.$t('tasks.taskType.screenshot'),
            dir: 'navigation',
            svg: 'screenshot',
          },
          11: {
            text: this.$t('tasks.taskType.message'),
            dir: 'navigation',
            svg: 'message',
          },
        },
      };
    },
    computed: {
      control: {
        get() {
          return !this.rfb.viewOnly;
        },
        set() {
          this.$set(this.rfb, 'viewOnly', !this.rfb.viewOnly);
        },
      },
      recordButtonText() {
        return this.isRecording ? 'Остановить запись' : 'Запись видео';
      },
      recordButtonIcon() {
        return this.isRecording ? 'stop' : 'play';
      },
      recordingTime() {
        const minutes = Math.floor(this.recordingDuration / 60);
        const seconds = this.recordingDuration % 60;

        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      },
      checkedComputers() {
        const { compId } = this.$route.params;
        return [
          {
            id: compId,
            name: this.name,
          },
        ];
      },
      zoomPercent() {
        return Math.round(this.zoom * 100);
      },
      screenStyle() {
        if (!this.scrollW || !this.scrollH) return {};
        return {
          width: `${Math.round(this.scrollW * this.zoom)}px`,
          height: `${Math.round(this.scrollH * this.zoom)}px`,
        };
      },
    },
    async created() {
      this.host = window.location.hostname;
      this.port = window.location.port;
      const { compId } = this.$route.params;
      const res = await getComputersAndVncKey();
      const { targetPort, key } = res;
      this.sourcePort = res.sourcePort;
      const computer = res.result.computers.find((comp) => comp.id === parseInt(compId, 10));
      this.name = computer.nameUser;
      const screen = document.getElementById('screen');

      this.setupFullscreen();

      console.log('Подключение по IP:', computer.ipAddress, computer);

      this.connect(computer.ipAddress, computer, targetPort, key, screen);
    },
    mounted() {
      this.updateScrollSize();
      window.addEventListener('resize', this.updateScrollSize);
      this.$nextTick(this.updateScrollSize);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.updateScrollSize);

      if (this.screenPointerHandler) {
        const screen = document.getElementById('screen');

        if (screen) {
          screen.removeEventListener('pointerdown', this.screenPointerHandler, true);
        }

        this.screenPointerHandler = null;
      }

      if (this.fullscreenChangeHandler) {
        document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
        this.fullscreenChangeHandler = null;
      }

      if (this.fullscreenKeyHandler) {
        document.removeEventListener('keydown', this.fullscreenKeyHandler);
        this.fullscreenKeyHandler = null;
      }

      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
        this.recordingTimer = null;
      }

      if (this.recordingAnimationFrame) {
        cancelAnimationFrame(this.recordingAnimationFrame);
        this.recordingAnimationFrame = null;
      }

      if (this.mediaRecorder) {
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.stop();
        }
      }

      if (this.recordingStream) {
        this.recordingStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (this.rfb) {
        this.rfb.disconnect();
        this.rfb = null;
      }
    },

    methods: {
      updateScrollSize() {
        const el = this.$refs.screenScroll;

        if (!el) return;

        this.scrollW = el.clientWidth;
        this.scrollH = el.clientHeight;
      },

      changeZoom(delta) {
        const next = Math.min(4, Math.max(0.5, +(this.zoom + delta).toFixed(2)));

        if (next === this.zoom) return;

        this.zoom = next;
        this.$nextTick(() => window.dispatchEvent(new Event('resize')));
      },

      onWheelZoom(e) {
        this.changeZoom(e.deltaY < 0 ? 0.15 : -0.15);
      },

      resetZoom() {
        this.zoom = 1;
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
          this.centerScroll();
        });
      },

      fitToScreen() {
        this.resetZoom();
      },

      centerScroll() {
        const el = this.$refs.screenScroll;

        if (!el) return;

        el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
        el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
      },

      focusHalf(side) {
        const el = this.$refs.screenScroll;

        if (!el) return;

        if (this.zoom < 1.75) {
          this.zoom = 2;
          this.$nextTick(() => window.dispatchEvent(new Event('resize')));
        }

        this.$nextTick(() => {
          const halfW = el.scrollWidth / 2;

          el.scrollLeft = side === 'left' ? 0 : halfW;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
        });
      },

      async toggleFullscreen() {
        const target = this.$refs.screenArea;

        if (!target) return;

        try {
          if (document.fullscreenElement) {
            await document.exitFullscreen();
          } else {
            await target.requestFullscreen();
          }
        } catch (error) {
          console.error('fullscreen toggle error:', error);
        }
      },

      takeScreenshot() {
        const canvas = this.$el.querySelector('#screen canvas');

        if (!canvas) {
          console.error('Canvas noVNC не найден');
          return;
        }

        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('Не удалось получить изображение canvas');
            return;
          }

          const filename = `screenshot-${this.name}-${dayjs().format('DD.MM.YYYY-HH-mm-ss')}.png`;

          forceFileDownload(blob, filename);
        }, 'image/png');
      },
      setupFullscreen() {
        const screenEl = document.getElementById('screen');

        if (!screenEl) return;

        if (this.screenPointerHandler) {
          screenEl.removeEventListener('pointerdown', this.screenPointerHandler, true);
        }

        this.screenPointerHandler = async () => {
          if (document.fullscreenElement) {
            return;
          }
          const target = this.$refs.screenArea;
          if (!target) return;

          try {
            await target.requestFullscreen();
            console.log('Fullscreen requested');
          } catch (error) {
            console.error('requestFullscreen error:', error);
          }
        };

        screenEl.addEventListener('pointerdown', this.screenPointerHandler, true);

        this.fullscreenChangeHandler = () => {
          this.isFullscreen = !!document.fullscreenElement;

          if (this.isFullscreen) {
            console.log('SCREEN IS FULLSCREEN');
          } else {
            console.log('SCREEN LEFT FULLSCREEN');
          }

          this.$nextTick(() => {
            this.updateScrollSize();
            window.dispatchEvent(new Event('resize'));
          });
        };

        document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);

        this.fullscreenKeyHandler = (e) => {
          if (!document.fullscreenElement) return;

          if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            this.changeZoom(0.25);
          } else if (e.key === '-' || e.key === '_') {
            e.preventDefault();
            this.changeZoom(-0.25);
          } else if (e.key === '0') {
            e.preventDefault();
            this.resetZoom();
          }
        };

        document.addEventListener('keydown', this.fullscreenKeyHandler);
      },
      startRecording() {
        if (this.isRecording || this.mediaRecorder) {
          return;
        }

        const sourceCanvas = this.$el.querySelector('#screen canvas');

        if (!sourceCanvas) {
          console.error('Canvas noVNC не найден');
          return;
        }

        const recordingCanvas = document.createElement('canvas');

        recordingCanvas.width = sourceCanvas.width;
        recordingCanvas.height = sourceCanvas.height;

        const context = recordingCanvas.getContext('2d');

        if (!context) {
          console.error('Не удалось получить 2D context');
          return;
        }

        this.recordingCanvas = recordingCanvas;
        this.recordingContext = context;
        this.recordedChunks = [];

        const drawFrame = () => {
          if (!this.isRecording) {
            return;
          }

          context.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height);

          this.recordingAnimationFrame = requestAnimationFrame(drawFrame);
        };

        this.isRecording = true;

        this.recordingStartedAt = Date.now();
        this.recordingDuration = 0;

        this.recordingTimer = setInterval(() => {
          if (this.recordingStartedAt) {
            this.recordingDuration = Math.floor((Date.now() - this.recordingStartedAt) / 1000);
          }
        }, 1000);

        drawFrame();

        const stream = recordingCanvas.captureStream(25);

        this.recordingStream = stream;

        const mimeType = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'].find((type) =>
          MediaRecorder.isTypeSupported(type),
        );

        if (!mimeType) {
          console.error('MediaRecorder не поддерживает WebM');

          this.isRecording = false;

          if (this.recordingAnimationFrame) {
            cancelAnimationFrame(this.recordingAnimationFrame);
            this.recordingAnimationFrame = null;
          }

          if (this.recordingTimer) {
            clearInterval(this.recordingTimer);
            this.recordingTimer = null;
          }

          this.recordingStartedAt = null;
          this.recordingDuration = 0;

          return;
        }

        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType,
        });

        this.mediaRecorder.addEventListener('dataavailable', (event) => {
          console.log('Video chunk:', event.data.size);

          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        });

        this.mediaRecorder.addEventListener('stop', () => {
          console.log('MediaRecorder stopped');
          console.log('Chunks:', this.recordedChunks.length);

          if (!this.recordedChunks.length) {
            console.error('Видео не содержит данных');
            this.cleanupRecording();
            return;
          }

          const blob = new Blob(this.recordedChunks, {
            type: mimeType,
          });

          console.log('Video size:', blob.size);

          const filename = `record-${this.name}-${dayjs().format('DD.MM.YYYY HH-mm-ss')}.webm`;

          forceFileDownload(blob, filename);

          this.cleanupRecording();
        });

        this.mediaRecorder.start(1000);

        console.log('Recording started');
      },

      stopRecording() {
        if (!this.mediaRecorder) {
          this.isRecording = false;
          return;
        }

        this.isRecording = false;

        if (this.recordingAnimationFrame) {
          cancelAnimationFrame(this.recordingAnimationFrame);
          this.recordingAnimationFrame = null;
        }

        if (this.recordingTimer) {
          clearInterval(this.recordingTimer);
          this.recordingTimer = null;
        }

        this.recordingStartedAt = null;
        this.recordingDuration = 0;

        if (this.mediaRecorder.state === 'recording') {
          console.log('Stopping recording...');

          this.mediaRecorder.stop();
        }
      },

      cleanupRecording() {
        if (this.recordingAnimationFrame) {
          cancelAnimationFrame(this.recordingAnimationFrame);
          this.recordingAnimationFrame = null;
        }

        if (this.recordingTimer) {
          clearInterval(this.recordingTimer);
          this.recordingTimer = null;
        }

        if (this.recordingStream) {
          this.recordingStream.getTracks().forEach((track) => {
            track.stop();
          });

          this.recordingStream = null;
        }

        this.mediaRecorder = null;
        this.recordingCanvas = null;
        this.recordingContext = null;
        this.recordedChunks = [];
        this.recordingStartedAt = null;
        this.recordingDuration = 0;
      },

      toggleRecording() {
        if (this.isRecording) {
          this.stopRecording();
        } else {
          this.startRecording();
        }
      },
      async addMessageTask(payload, type) {
        this.payloads.push(payload);
        this.isLoading = true;
        try {
          await addTask(type, this.checkedComputers, this.payloads);
        } catch (err) {
          console.error(err);
        } finally {
          this.isLoading = false;
        }
        this.closeModal();
      },
      async addTaskForComputers(type) {
        if (type === '12') {
          this.toggleRecording();
        } else if (type === '10') {
          try {
            this.takeScreenshot();
          } catch (err) {
            console.error(err);
          }
        } else {
          this.payloads = [];
          this.currentComputer = {
            index: 0,
            value: this.checkedComputers[0],
          };
          this.openModal();
        }
      },

      closeModal() {
        this.isOpenModal = false;
      },
      openModal() {
        this.isOpenModal = true;
      },
      connect(compHost, computer, targetPort, key, screen) {
        let currentHost = compHost;
        let port = targetPort;
        if (compHost.includes(':')) {
          const [vncHost, vncPort] = compHost.split(':');
          currentHost = vncHost;
          port = vncPort;
        }
        const token = jwt.sign({ host: currentHost, port }, key);
        const currentPort = this.port ? `:${this.port}` : '';
        console.log(window.location);
        console.log('port:', this.port);
        console.log('currentPort:', currentPort);

        const url = `${'wss://'}${this.host}${currentPort}/ws/${token}`;

        console.log(url);

        this.rfb = new RFB(screen, url, { credentials: { password: computer.vncPass } });

        this.rfb.addEventListener('connect', (e) => {
          if (e) {
            this.status = 'connected';
            console.log('Подключение:', this.temp);
          }
        });

        this.rfb.addEventListener('disconnect', (e) => {
          if (e) {
            if (e.detail.clean) {
              this.status = 'disconnected';
            } else {
              this.status = 'error';
              console.log('Ошибка:', this.temp);
            }
          }
          if (this.status === 'error' && this.temp < 2) {
            console.log('Подключение Error:', this.temp);
            this.status = 'connecting';
            this.temp += 1;

            console.log('Подключение по имени:', computer.nameComputer, computer);
            this.connect(computer.nameComputer, computer, targetPort, key, screen);
          }
        });

        this.rfb.addEventListener('clipboard', async (e) => {
          if (e) {
            await navigator.clipboard.writeText(e.detail.text);
          }
        });

        screen.addEventListener(
          'keydown',
          async (e) => {
            if (e.code === 'KeyV') {
              const clipboard = await navigator.clipboard.readText();
              this.rfb.clipboardPasteFrom(clipboard);
            }
          },
          true,
        );

        this.rfb.viewOnly = true;
        this.rfb.showDotCursor = true;
        this.rfb.resizeSession = true;
        this.rfb.scaleViewport = true;
        this.rfb.clipViewport = false;
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .status {
    text-align: center;
    padding: 5px;

    &.green {
      background: $green;
    }

    &.yellow {
      background: $yellow;
    }

    &.red {
      background: $red;
    }
  }

  .description {
    border: 1px solid $borderColor;
    border-bottom: 0px;
    display: flex;
    flex-direction: column;
  }

  .sreenContainer {
    display: block;
    position: relative;
  }

  .container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .toggleContainer {
    margin: 20px;
    cursor: default;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .wrapper {
    display: flex;
    flex-shrink: 0;
  }

  .actionButton {
    margin: 10px;
    border: 1px solid var(--ui-border);
    border-radius: 9px;
    min-width: 110px;
    flex-shrink: 0;
    min-height: 58px;
    padding: 9px 10px;
    gap: 4px;
    font-size: 13px;
    line-height: 1.25;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    -webkit-hyphens: none;
    -ms-hyphens: none;
    hyphens: none;
    background: white;
    height: 70px;

    &:hover {
      transition: background-color 0.5s linear;
      color: var(--ui-primary);
      border-color: #a7caf2;
      background: var(--ui-primary-soft);
      transform: translateY(-1px);
    }
  }

  .recordingButton {
    border-color: $red;
  }

  .recordingIcon {
    display: inline-block;
    flex-shrink: 0;
  }

  .playIcon {
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 11px solid currentColor;
  }

  .stopIcon {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: currentColor;
  }

  .recordingIndicator {
    width: 8px;
    height: 8px;
    margin-bottom: 4px;
    border-radius: 50%;
    background: $red;
    animation: recordingPulse 1s infinite;
  }

  @keyframes recordingPulse {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 0.35;
    }

    100% {
      opacity: 1;
    }
  }

  .inline {
    display: inline-flex;
    height: 18px;
  }

  .buttonText {
    padding: 0px 7px;
  }
  .modal {
    z-index: 3000;
  }

  .screenArea {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    &:fullscreen {
      background: #000;
      width: 100vw;
      height: 100vh;
    }

    &:-webkit-full-screen {
      background: #000;
      width: 100vw;
      height: 100vh;
    }
  }

  .screenScroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
    background: #000;
    position: relative;
  }

  .zoomBar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 6px 10px;
    background: #f7f9fc;
    border: 1px solid $borderColor;
    border-bottom: 0;
  }

  .zoomBtn {
    min-width: 32px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid var(--ui-border);
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    line-height: 1;

    &:hover {
      background: var(--ui-primary-soft);
      border-color: #a7caf2;
    }
  }

  .zoomValue {
    min-width: 48px;
    text-align: center;
    font-size: 13px;
    color: #666;
  }

  .zoomHint {
    margin-left: auto;
    font-size: 12px;
    color: #888;
  }
</style>
